import { OIL_DATABASE } from "@/data/oilDatabase"

export function SeoContent() {
  return (
    <>
      {/* SEO Content Section */}
      <section className="max-w-[1800px] mx-auto px-4 py-12">
        {/* Main SEO Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Calculator Soap: The Modern Soap Calculator & Lye Tool
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            A faster, mobile-friendly alternative to SoapCalc for Cold Process and Hot Process soap making.
          </p>
        </div>

        {/* Why Use Calculator Soap */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Why Calculator Soap?</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Calculator Soap provides <strong>clean math</strong> and an exceptional{" "}
              <strong>user experience</strong>. Unlike legacy tools, our soap making calculator
              delivers real-time quality metrics, a modern interface, and mobile-first design.
              Whether you're a beginner or professional soap maker, our lye calculator gives you
              precision and confidence in every batch.
            </p>
          </div>

          <div id="safety" className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Lye Safety for Beginners</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              <strong>Safety is priority #1</strong> when handling lye (sodium hydroxide or
              potassium hydroxide). Always wear protective gear: goggles, gloves, and long
              sleeves. Never add water to lye—always add lye to liquid. Work in a well-ventilated
              area. Our lye calculator for beginners ensures accurate measurements every time,
              reducing risk and improving results.
            </p>
          </div>

          <div id="beginners" className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Soap Recipe Calculator</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              This soap recipe builder works for <strong>Cold Process</strong>,{" "}
              <strong>Hot Process</strong>, and <strong>Liquid soap</strong> formulas. Select your
              oils, set your superfat percentage, and let our calculator do the rest. The built-in
              quality metrics help you create balanced recipes with the perfect blend of hardness,
              lather, and conditioning.
            </p>
          </div>
        </div>

        {/* Oil Properties Chart */}
        <div id="properties" className="bg-white rounded-xl shadow-md p-6 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Oil Properties Chart</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Oil</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">SAP (NaOH)</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Hardness</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Cleansing</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Conditioning</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Bubbly</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Creamy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {OIL_DATABASE.map((oil) => (
                  <tr key={oil.name} className="hover:bg-gray-50">
                    <td className="py-2 px-4 font-medium text-gray-900">{oil.name}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.sapNaOH}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.hardness}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.cleansing}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.conditioning}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.bubbly}</td>
                    <td className="text-center py-2 px-4 text-gray-700">{oil.creamy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Best SoapCalc Alternative */}
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            The Best SoapCalc Alternative
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">📱</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Mobile-Friendly</h3>
              <p className="text-sm text-gray-600">
                Use your phone right in the soap lab. Touch-optimized controls and responsive
                design make formulating easy on any device.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">📊</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Real-Time Quality Metrics</h3>
              <p className="text-sm text-gray-600">
                See hardness, cleansing, conditioning, and lather scores update instantly as you
                adjust your recipe. No more guessing.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">🎨</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Modern Interface</h3>
              <p className="text-sm text-gray-600">
                A clean, professional dashboard that makes soap recipe building a pleasure. Save,
                export, and print your recipes with ease.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-[1800px] mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Calculator Soap</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                The modern soap calculator and lye tool for professional and hobbyist soap makers.
                Free online soap maker with real-time calculations.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Tools</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Soap Making Formula</a></li>
                <li><a href="#" className="hover:text-white transition-colors">How to Calculate Lye</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Lye Calculator for Cold Process</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Soap Recipe Builder</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#safety" className="hover:text-white transition-colors">Lye Safety Guide</a></li>
                <li><a href="#beginners" className="hover:text-white transition-colors">Soap Making for Beginners</a></li>
                <li><a href="#properties" className="hover:text-white transition-colors">Oil Properties Chart</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Legal</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                ⚠️ <strong>Lye Safety Disclaimer:</strong> Always follow proper safety procedures
                when handling lye. Wear protective equipment including goggles, gloves, and long
                sleeves. This tool provides calculations only and is not a substitute for proper
                safety training.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-500">
            <p>
              © {new Date().getFullYear()} Calculator Soap — Free Online Soap Maker | Lye
              Calculator for Cold Process | Soap Recipe Builder
            </p>
            <meta
              name="keywords"
              content="soapcalc, soap making calculator, lye calculator for beginners, soap mold volume calculator, soap recipe builder"
            />
          </div>
        </div>
      </footer>
    </>
  )
}
